import React from "react";
import { LOGISTIC_TABLE_COLUMNS, TABLE_COLUMNS } from "constants/translates";
import { User } from "models/Users/User";
import { Logistic } from "models/Logistic";
import TextInput from "components/tables/logisticTable/columns/TextInput";
import DatePicker from "components/tables/logisticTable/columns/DatePicker";
import moment from "moment";
import AutoComplete from "components/tables/logisticTable/columns/AutoComplete";
import TextArea from "components/tables/logisticTable/columns/TextArea";
import Select from "components/tables/logisticTable/columns/SelectField";
import Checkbox from "components/tables/logisticTable/columns/Checkbox";
import UploadFile from "components/tables/logisticTable/columns/UploadFile";
import Actions from "./columns/actions";
import { Column, IHasChildren } from "../../common/Table/Interfaces";
import LogisticStore from "stores/LogisticStore";
import { Option, SelectOption } from "components/common/forms/Interfaces";
import SearchFilter from "components/tables/declarantTable/filters/searchFilter";
import DateTimeFilter from "components/tables/declarantTable/filters/dateTimeFilter";
import ClientFilter from "components/tables/declarantTable/filters/selectColumns/client";
import SelectFilter from "components/tables/declarantTable/filters/selectFilter";
import { AttachedFile } from "components/tables/logisticTable/columns/AttachedFile";
import UserStore from "stores/UserStore";
import { ROLE_CLIENT } from "constants/authorize";
import { DELETE_LOGISTIC_FILE, DOWNLOAD_LOGISTIC_FILE } from "constants/events";
import { RuleObject, StoreValue } from 'rc-field-form/lib/interface';

type UserWithLogistic = (IHasChildren<Logistic> & User) | Logistic;

const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
  { value: "PLN", label: "PLN" },
] as SelectOption[];

const IS_PAID_FILTER_OPTIONS = [
  { value: "true", title: "Yes" },
  { value: "false", title: "No" },
] as Option[];

const requiredFieldValidation = {
  validationRules: [
    {
      required: true,
      message: "Required field",
    },
  ],
};

const amountFieldValidation = {
  validationRules: [
    {
      required: true,
      validator: (_: RuleObject, value: StoreValue) =>
          isNaN(value)
              ? Promise.reject()
              : Promise.resolve(),
      message: "Required field",
    },
  ],
};

const COLUMNS = (
  form: any,
  clients: Option[],
  userStore: UserStore
): Column[] => {
  const {
    editingKeys,
    countries,
    tcOptions,
    contractorOptions,
  } = LogisticStore;

  const setFieldValue = (fieldName: string, entity: Logistic, value: any) => {
    const stateValues = form.getFieldsValue();

    if (stateValues?.[`${fieldName}_${entity.id}`] === undefined) {
      form.setFieldsValue({
        [`${fieldName}_${entity.id}`]: value,
      });
    }
  };

  return [
    {
      title: TABLE_COLUMNS.INDEX,
      dataIndex: "id",
      key: "id",
      sorter: false,
      isActive: true,
      order: 2,
      width: 60,
      editable: false,
      render: (text, entity: UserWithLogistic, index) => {
        if ((entity as IHasChildren<Logistic>).children) return null;

        return (entity as IHasChildren<Logistic>).children ? null : index + 1;
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.internalNo,
      dataIndex: "internal_number",
      key: "internal_number",
      sorter: true,
      order: 4,
      width: 150,
      ellipsis: true,
      isActive: true,
      editable: true,
      inputType: "text",
      filterDropdown: SearchFilter,
      render: (text, entity: Logistic) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("internal_number", entity, entity.internal_number);

          return (
            <TextInput value={text} name={`internal_number_${entity.id}`} />
          );
        }

        return entity.internal_number;
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.loading_date,
      dataIndex: "loading_date",
      key: "loading_date",
      sorter: true,
      order: 5,
      width: 150,
      ellipsis: true,
      isActive: true,
      editable: false,
      filterDropdown: DateTimeFilter,
      render: (text, entity: Logistic) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("loading_date", entity, moment(entity.loading_date));

          return (
            <DatePicker
              value={moment(text)}
              name={`loading_date_${entity.id}`}
              config={requiredFieldValidation}
            />
          );
        }

        return entity.loading_date;
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.docs_sent_date,
      dataIndex: "docs_sent_date",
      key: "docs_sent_date",
      sorter: true,
      order: 6,
      width: 180,
      ellipsis: true,
      isActive: true,
      editable: false,
      filterDropdown: DateTimeFilter,
      render: (text, entity: Logistic) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        const entityDate = entity.docs_sent_date
          ? moment(entity.docs_sent_date)
          : "";

        if (editMode) {
          setFieldValue("docs_sent_date", entity, entityDate);

          return (
            <DatePicker
              value={moment(text)}
              name={`docs_sent_date_${entity.id}`}
            />
          );
        }

        return entity.docs_sent_date;
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.tc,
      dataIndex: "tc",
      key: "tc",
      sorter: true,
      order: 6,
      width: 150,
      ellipsis: true,
      isActive: true,
      editable: false,
      filterDropdown: SearchFilter,
      render: (text, entity: Logistic) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("tc", entity, entity.tc);

          return (
            <AutoComplete
              defaultValue={text}
              name={`tc_${entity.id}`}
              defaultOptions={tcOptions}
              config={requiredFieldValidation}
            />
          );
        }

        return entity.tc;
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.fv,
      dataIndex: "fv",
      key: "fv",
      sorter: true,
      order: 7,
      width: 150,
      ellipsis: true,
      isActive: true,
      editable: true,
      inputType: "text",
      filterDropdown: SearchFilter,
      render: (text, entity: Logistic) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("fv", entity, entity.fv);

          return <TextInput value={text} name={`fv_${entity.id}`} />;
        }

        return entity.fv;
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.way,
      dataIndex: "way",
      key: "way",
      order: 8,
      width: 360,
      ellipsis: true,
      isActive: true,
      editable: false,
      render: (text, entity: Logistic) => {
        const defaultValueFrom = countries.find(
          (option) => option.value === entity.way_from
        );

        const defaultValueTo = countries.find(
          (option) => option.value === entity.way_to
        );

        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("way_from", entity, defaultValueFrom?.value);
          setFieldValue("way_to", entity, defaultValueTo?.value);
          setFieldValue("way_text", entity, entity?.way_text);

          return (
            <div className="way-fields-wrapper">
              <div className="way-from-to-container">
                <Select
                  defaultValue={entity.way_from}
                  name={`way_from_${entity.id}`}
                  options={countries}
                  config={requiredFieldValidation}
                  width="45%"
                  className="table-select"
                  size="small"
                />
                <div style={{ padding: "5px" }}>-</div>
                <Select
                  defaultValue={entity.way_to}
                  name={`way_to_${entity.id}`}
                  options={countries}
                  config={requiredFieldValidation}
                  width="45%"
                  className="table-select"
                  size="small"
                />
              </div>
              <TextInput
                value={text}
                name={`way_text_${entity.id}`}
                height="24px"
              />
            </div>
          );
        }

        return (
          <div className="way-fields-wrapper">
            {defaultValueFrom?.label && defaultValueTo?.label && (
              <div>
                {`${defaultValueFrom?.label || ""} - ${defaultValueTo?.label}`}
              </div>
            )}
            {entity?.way_text && <div>{entity?.way_text}</div>}
          </div>
        );
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.note,
      dataIndex: "note",
      key: "note",
      sorter: true,
      order: 9,
      width: 200,
      ellipsis: true,
      isActive: true,
      editable: false,
      filterDropdown: SearchFilter,
      render: (text, entity: Logistic) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("note", entity, entity.note);

          return <TextArea value={text} name={`note_${entity.id}`} />;
        }

        return entity.note;
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.amount,
      dataIndex: "amount",
      key: "amount",
      sorter: true,
      order: 10,
      width: 120,
      ellipsis: true,
      isActive: true,
      editable: false,
      filterDropdown: SearchFilter,
      render: (text, entity: Logistic) => {
        const asClient = userStore?.authorizeByRole(ROLE_CLIENT);
        const editMode = editingKeys?.some((key) => key === entity.id);
        const disabled = asClient && entity?.client_id !== entity?.user_id;

        if (editMode) {
          setFieldValue("amount", entity, entity.amount || 0);

          return (
            <TextInput
              value={text}
              name={`amount_${entity.id}`}
              type="number"
              disabled={disabled}
              config={amountFieldValidation}
            />
          );
        }

        return entity.amount > 0 ? entity.amount : "";
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.currency,
      dataIndex: "currency",
      key: "currency",
      sorter: true,
      order: 11,
      width: 120,
      ellipsis: true,
      isActive: true,
      editable: false,
      render: (text, entity: Logistic) => {
        const defaultValue = CURRENCY_OPTIONS.find(
          (option) => option.value === entity.currency
        );

        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("currency", entity, defaultValue?.value);

          return (
            <Select
              defaultValue={text}
              name={`currency_${entity.id}`}
              options={CURRENCY_OPTIONS}
              config={requiredFieldValidation}
            />
          );
        }

        return defaultValue?.label;
      },
      filterDropdown: SearchFilter,
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.payment_date,
      dataIndex: "payment_date",
      key: "payment_date",
      sorter: true,
      order: 12,
      width: 150,
      ellipsis: true,
      isActive: true,
      editable: false,
      filterDropdown: DateTimeFilter,
      render: (text, entity: Logistic) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("payment_date", entity, moment(entity.payment_date));

          return (
            <DatePicker
              value={moment(text)}
              name={`payment_date_${entity.id}`}
              config={requiredFieldValidation}
            />
          );
        }

        return entity.payment_date;
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.is_paid,
      dataIndex: "is_paid",
      key: "is_paid",
      sorter: true,
      order: 13,
      width: 80,
      ellipsis: true,
      isActive: true,
      editable: false,
      render: (text, entity: Logistic) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("is_paid", entity, Boolean(entity.is_paid));
        }

        return (
          <Checkbox
            defaultChecked={Boolean(text)}
            name={
              editMode ? `is_paid_${entity.id}` : `is_paid_view_${entity.id}`
            }
            disabled={!editMode}
            form={form}
          />
        );
      },
      filterDropdown: ({
        setSelectedKeys,
        confirm,
        clearFilters,
        selectedKeys,
      }) => {
        return (
          <SelectFilter
            setSelectedKeys={setSelectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            options={IS_PAID_FILTER_OPTIONS}
            selectedKeys={selectedKeys}
          />
        );
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.is_ready,
      dataIndex: "is_ready",
      key: "is_ready",
      sorter: true,
      order: 13,
      width: 80,
      ellipsis: true,
      isActive: true,
      editable: false,
      render: (text, entity: Logistic) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("is_ready", entity, Boolean(entity.is_ready));
        }

        return (
          <Checkbox
            defaultChecked={Boolean(text)}
            name={
              editMode ? `is_ready_${entity.id}` : `is_ready_view_${entity.id}`
            }
            disabled={!editMode}
            form={form}
          />
        );
      },
      filterDropdown: ({
        setSelectedKeys,
        confirm,
        clearFilters,
        selectedKeys,
      }) => {
        return (
          <SelectFilter
            setSelectedKeys={setSelectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            options={IS_PAID_FILTER_OPTIONS}
            selectedKeys={selectedKeys}
          />
        );
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.client,
      dataIndex: "client_id",
      key: "client_id",
      sorter: true,
      order: 14,
      width: 150,
      ellipsis: true,
      render: (text, entity: Logistic) => {
        const asClient = userStore?.authorizeByRole(ROLE_CLIENT);
        let disabled = false;

        const clientOptions = clients?.map(
          (client: Option): SelectOption => ({
            value: client.value,
            label: client.title,
          })
        );

        const defaultValue = clientOptions.find(
          (option) => option.value === entity.client_id
        );

        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("client_id", entity, defaultValue?.value);

          if (asClient) {
            const defaultValue = userStore?.user?.id;
            setFieldValue("client_id", entity, defaultValue);
            disabled = true;
          }

          return (
            <Select
              defaultValue={text}
              name={`client_id_${entity.id}`}
              options={clientOptions}
              disabled={disabled}
            />
          );
        }

        return defaultValue?.label;
      },
      filterDropdown: ({
        setSelectedKeys,
        confirm,
        clearFilters,
        selectedKeys,
      }) => (
        <ClientFilter
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          selectedKeys={selectedKeys}
          multiple
        />
      ),
      filterMultiple: true,
      editable: false,
      isActive: true,
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.contractor,
      dataIndex: "contractor",
      key: "contractor",
      sorter: true,
      order: 15,
      width: 160,
      ellipsis: true,
      isActive: true,
      editable: false,
      filterDropdown: SearchFilter,
      render: (text, entity: Logistic) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("contractor", entity, entity.contractor);

          return (
            <AutoComplete
              defaultValue={text}
              name={`contractor_${entity.id}`}
              defaultOptions={contractorOptions}
            />
          );
        }

        return entity.contractor;
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.documents,
      dataIndex: "documents",
      key: "documents",
      sorter: true,
      order: 16,
      width: 400,
      ellipsis: true,
      isActive: true,
      editable: false,
      render: (text, entity: Logistic) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("documents", entity, entity.documents);

          return (
            <>
              <AttachedFile
                documents={entity.documents}
                id={entity.id}
                downloadEventId={DOWNLOAD_LOGISTIC_FILE}
                deleteEventId={DELETE_LOGISTIC_FILE}
                deleteFile
              />
              <UploadFile defaultValue={text} name={`documents_${entity.id}`} />
            </>
          );
        }

        return (
          <AttachedFile
            documents={entity.documents}
            id={entity.id}
            downloadEventId={DOWNLOAD_LOGISTIC_FILE}
            deleteEventId={DELETE_LOGISTIC_FILE}
          />
        );
      },
    },
    {
      title: LOGISTIC_TABLE_COLUMNS.actions,
      dataIndex: "actions",
      key: "actions",
      sorter: false,
      order: 9999,
      width: 180,
      ellipsis: true,
      render: (text, entity: Logistic) => {
        return (
          <Actions entity={entity} isTemporary={entity.temporary} form={form} />
        );
      },
      isActive: true,
    },
  ] as Column[];
};

export default COLUMNS;
